'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { getActiveMacroSection } from './TopBar';
import GlobalSearch from './GlobalSearch';
import OnboardingTour from './OnboardingTour';
import CommandPalette from './CommandPalette';
import Breadcrumb from './Breadcrumb';
import AuthGuard from './AuthGuard';
import { NotificationCenterPanel, useNotificationCenter } from '@/context/NotificationCenterContext';
import { Menu, X, Search, Sun, Moon, Keyboard, Zap, Globe, Bell, Command, LogOut } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { useKeyboardShortcuts, KEYBOARD_SHORTCUTS } from '@/hooks/useKeyboardShortcuts';
import { LATEST_VERSION } from '@/app/changelog/page';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';

interface AppLayoutProps {
  children: React.ReactNode;
}

const SEEN_VERSION_KEY = 'lcm_seen_version';

export default function AppLayout({ children }: AppLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [hasNewChangelog, setHasNewChangelog] = useState(false);
  const { toggleTheme, isDark } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const { unreadCount } = useNotificationCenter();
  const { logout } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useKeyboardShortcuts({
    onOpenSearch: () => setSearchOpen(true),
    onToggleDark: toggleTheme,
    onOpenCommand: () => setCommandOpen(true),
  });

  // Check for new changelog version
  useEffect(() => {
    const seen = localStorage.getItem(SEEN_VERSION_KEY);
    if (seen !== LATEST_VERSION) {
      setHasNewChangelog(true);
    }
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, []);

  const dismissChangelogBadge = () => {
    localStorage.setItem(SEEN_VERSION_KEY, LATEST_VERSION);
    setHasNewChangelog(false);
  };

  // Request notification permission on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      // Don't auto-request — wait for user action
    }
  }, []);

  const notifAriaLabel = unreadCount > 0
    ? `${t('appLayout.notificationsLabel')} (${unreadCount} ${t('appLayout.notificationsUnread')})`
    : t('appLayout.notificationsLabel');

  const activeSection = getActiveMacroSection(pathname);

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {/* Skip to main content — keyboard accessibility */}
      <a href="#main-content" className="skip-link focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        {t('appLayout.skipToContent')}
      </a>

      {/* Global Search Modal */}
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Command Palette */}
      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />

      {/* Notification Center */}
      <NotificationCenterPanel open={notifOpen} onClose={() => setNotifOpen(false)} />

      {/* Onboarding Tour */}
      <OnboardingTour autoStart />

      {/* Keyboard shortcuts modal */}
      {shortcutsOpen && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShortcutsOpen(false)}
        >
          <div
            className="bg-white dark:bg-card rounded-2xl shadow-2xl border border-border w-full max-w-sm p-6"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t('appLayout.shortcutsTitle')}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Keyboard size={16} className="text-primary" aria-hidden="true" />
                {t('appLayout.keyboardShortcuts')}
              </h2>
              <button
                onClick={() => setShortcutsOpen(false)}
                aria-label={t('appLayout.closeShortcuts')}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <X size={15} aria-hidden="true" />
              </button>
            </div>
            <div className="space-y-1">
              {KEYBOARD_SHORTCUTS.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <span className="text-sm text-foreground">{s.description}</span>
                  <div className="flex items-center gap-1">
                    {s.keys.map((k, ki) => (
                      <React.Fragment key={ki}>
                        <kbd className="px-2 py-0.5 bg-muted border border-border rounded-md text-xs font-mono text-foreground">{k}</kbd>
                        {ki < s.keys.length - 1 && <span className="text-muted-foreground text-xs">+</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Top macro-navigation bar */}
      <TopBar activeSection={activeSection} />

      {/* Body: sidebar + main */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div
          id="sidebar-nav"
          className={`fixed inset-y-0 left-0 z-50 lg:static lg:z-auto transition-transform duration-300 ease-in-out ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
          aria-label={t('sidebar.ariaLabel')}
        >
          <Sidebar />
        </div>

        {/* Main content */}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0" id="main-content">
          {/* Top utility bar */}
          <div className="flex items-center h-14 px-4 border-b border-border bg-white dark:bg-card shrink-0 gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              aria-label={t('appLayout.openNav')}
              aria-expanded={mobileOpen}
              aria-controls="sidebar-nav"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-foreground"
            >
              <Menu size={18} aria-hidden="true" />
            </button>

            {mobileOpen && (
              <button
                onClick={() => setMobileOpen(false)}
                aria-label={t('appLayout.closeNav')}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-foreground"
              >
                <X size={18} aria-hidden="true" />
              </button>
            )}

            {/* Search bar */}
            <button
              onClick={() => setSearchOpen(true)}
              aria-label={t('appLayout.searchAriaLabel')}
              className="flex items-center gap-2.5 flex-1 max-w-md px-3.5 py-2 bg-muted/60 hover:bg-muted border border-border/60 hover:border-border rounded-xl text-sm text-muted-foreground transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Search size={14} className="shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline text-[13px]">{t('appLayout.searchPlaceholder')}</span>
              <span className="sm:hidden text-[13px]">{t('appLayout.searchPlaceholderShort')}</span>
              <kbd className="ml-auto hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 bg-white dark:bg-card border border-border rounded-md text-[10px] font-mono text-muted-foreground">
                ⌘K
              </kbd>
            </button>

            <div className="flex items-center gap-0.5 ml-auto">
              {/* Changelog badge */}
              {hasNewChangelog && (
                <Link
                  href="/changelog"
                  onClick={dismissChangelogBadge}
                  aria-label={`${t('appLayout.newVersion')}: v${LATEST_VERSION}`}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-primary/10 text-primary-dark rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary mr-1"
                >
                  <Zap size={12} aria-hidden="true" />
                  <span className="hidden sm:inline">v{LATEST_VERSION}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                </Link>
              )}

              {/* Command palette button */}
              <button
                onClick={() => setCommandOpen(true)}
                aria-label={t('appLayout.commandPalette')}
                title={t('appLayout.commandPalette')}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-foreground"
              >
                <Command size={16} aria-hidden="true" />
              </button>

              {/* Notification bell */}
              <button
                onClick={() => setNotifOpen(v => !v)}
                aria-label={notifAriaLabel}
                aria-expanded={notifOpen}
                title={t('appLayout.notificationsCenter')}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-foreground"
              >
                <Bell size={16} aria-hidden="true" />
                {unreadCount > 0 && (
                  <span
                    className="absolute top-1.5 right-1.5 w-4 h-4 bg-primary text-white text-[9px] font-bold rounded-full flex items-center justify-center"
                    aria-hidden="true"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Keyboard shortcuts button */}
              <button
                onClick={() => setShortcutsOpen(true)}
                aria-label={t('appLayout.showShortcuts')}
                title={t('appLayout.shortcutsTitle')}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-foreground"
              >
                <Keyboard size={16} aria-hidden="true" />
              </button>

              {/* Dark mode toggle */}
              <button
                onClick={toggleTheme}
                aria-label={isDark ? t('appLayout.lightMode') : t('appLayout.darkMode')}
                title={isDark ? t('appLayout.lightMode') : t('appLayout.darkMode')}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-foreground"
              >
                {isDark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
              </button>

              {/* Language switcher */}
              <div className="relative group">
                <button
                  aria-label={language === 'it' ? t('appLayout.currentLanguageIt') : t('appLayout.currentLanguageEn')}
                  aria-haspopup="listbox"
                  title={t('appLayout.changeLanguage')}
                  className="flex items-center gap-1 h-9 px-2.5 rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-foreground text-xs font-medium"
                >
                  <Globe size={14} aria-hidden="true" />
                  <span className="uppercase text-[11px]">{language}</span>
                </button>
                <div
                  className="absolute right-0 top-full mt-1.5 w-32 bg-white dark:bg-card border border-border rounded-xl shadow-modal overflow-hidden opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity z-50"
                  role="listbox"
                  aria-label={t('appLayout.selectLanguage')}
                >
                  {(['it', 'en'] as const).map(lang => (
                    <button
                      key={lang}
                      onClick={() => setLanguage(lang)}
                      role="option"
                      aria-selected={language === lang}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm transition-colors ${language === lang ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted'}`}
                    >
                      <span className="text-base" aria-hidden="true">{lang === 'it' ? '🇮🇹' : '🇬🇧'}</span>
                      {lang === 'it' ? 'Italiano' : 'English'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logout button */}
              <button
                onClick={async () => {
                  await logout();
                  router.push('/login');
                }}
                aria-label={t('appLayout.logout')}
                title={t('appLayout.logout')}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground hover:text-red-500"
              >
                <LogOut size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Breadcrumb */}
          <Breadcrumb />

          <div className="flex-1 overflow-y-auto scrollbar-thin">
            <AuthGuard>
              {children}
            </AuthGuard>
          </div>
        </main>
      </div>
    </div>
  );
}